import { useState } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Link } from "@tanstack/react-router";
import { getStripe, getStripeEnvironment } from "@/lib/stripe";
import { createMembershipCheckout } from "@/lib/shop.functions";
import { track } from "@/lib/analytics";
import type { MembershipPriceId } from "@/lib/membership";

interface Props {
  priceId: MembershipPriceId;
  returnUrl?: string;
}

export function StripeEmbeddedMembershipCheckout({ priceId, returnUrl }: Props) {
  const [consent, setConsent] = useState({ terms: false, privacy: false, refund: false, subscription: false });
  const [proceed, setProceed] = useState(false);
  const isSubscription = priceId !== "lifetime_ascension_pass";
  const ready = consent.terms && consent.privacy && consent.refund && (isSubscription ? consent.subscription : true);

  const fetchClientSecret = async (): Promise<string> => {
    track("checkout_start", { priceId, kind: isSubscription ? "subscription" : "one_time" });
    const result = await createMembershipCheckout({
      data: {
        priceId,
        returnUrl: returnUrl ?? `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`,
        environment: getStripeEnvironment(),
        consent: {
          terms: true as const,
          privacy: true as const,
          refund: true as const,
          // Server requires subscription: true for recurring; lifetime is one-off.
          // We pass it as true regardless — the schema is per-flow but the UI enforces above.
          subscription: true as const,
        },
      },
    });
    if ("error" in result) throw new Error(result.error);
    if (!result.clientSecret) throw new Error("Stripe did not return a client secret");
    return result.clientSecret;
  };

  if (proceed) {
    return (
      <div id="checkout" className="rounded-lg border hairline bg-obsidian/40 p-2">
        <EmbeddedCheckoutProvider stripe={getStripe()} options={{ fetchClientSecret }}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    );
  }

  return (
    <div className="rounded-lg border hairline bg-obsidian/40 p-6 space-y-5">
      <fieldset className="space-y-2">
        <legend className="text-xs uppercase tracking-[0.25em] text-gold mb-2">Required consent</legend>
        <ConsentCheckbox
          checked={consent.terms}
          onChange={(v) => setConsent((c) => ({ ...c, terms: v }))}
          label={<>I agree to the <Link to="/terms" className="text-gold underline">Terms of Service</Link>.</>}
        />
        <ConsentCheckbox
          checked={consent.privacy}
          onChange={(v) => setConsent((c) => ({ ...c, privacy: v }))}
          label={<>I agree to the <Link to="/privacy" className="text-gold underline">Privacy Policy</Link>.</>}
        />
        <ConsentCheckbox
          checked={consent.refund}
          onChange={(v) => setConsent((c) => ({ ...c, refund: v }))}
          label={<>I have read and accept the <Link to="/refund" className="text-gold underline">Refund Policy</Link>.</>}
        />
        {isSubscription && (
          <ConsentCheckbox
            checked={consent.subscription}
            onChange={(v) => setConsent((c) => ({ ...c, subscription: v }))}
            label={<>I understand this is a recurring subscription that auto-renews until I cancel from my account.</>}
          />
        )}
      </fieldset>

      <button
        type="button"
        disabled={!ready}
        onClick={() => setProceed(true)}
        className="w-full rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold disabled:cursor-not-allowed disabled:opacity-50"
      >
        Continue to secure payment
      </button>
    </div>
  );
}

function ConsentCheckbox({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: React.ReactNode }) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-gold" />
      <span className="text-muted-foreground">{label}</span>
    </label>
  );
}
